import { Box, Container, Header, Spinner } from '@amzn/awsui-components-core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchGraphQLRequest } from 'src/api/graphql-api';
import { AnnouncementEntity } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { listTestimonials } from 'src/graphql/queries';
import './TestimonialsCarousel.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPen } from '@fortawesome/free-solid-svg-icons';

export interface TestimonialEntity {
  id: string;
  announcement_id: string;
  announcement_name: string;
  testimonial_sequence: number;
  original_text: string;
  summary: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface TestimonialsCarouselProps {
  maxItems?: number;
  autoRotateInterval?: number;
}

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Parse summary into structured parts: title, quote, and attribution.
 * Expected format: [Title] "quote text" –Name, Title
 * Handles smart quotes (\u201c \u201d) and straight quotes.
 */
const parseTestimonialSummary = (summary: string): { title: string; quote: string; attribution: string } | null => {
  // Match: [Title] followed by quoted text and optional attribution
  const match = summary.match(/^(\[.*?\])\s*["\u201c\u201f](.*?)["\u201d\u201e]\s*([\u2013\u2014\u2012–-].+)?$/s);
  if (match) {
    return {
      title: match[1].replace(/^\[|\]$/g, ''), // Remove square brackets
      quote: match[2].trim(),
      attribution: match[3]?.trim().replace(/^[\u2013\u2014\u2012–-]\s*/, '') || ''
    };
  }
  return null;
};

const TestimonialsCarousel: React.FC<TestimonialsCarouselProps> = ({ maxItems = 20, autoRotateInterval = 12000 }) => {
  const [testimonials, setTestimonials] = useState<TestimonialEntity[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const appContext = useAppContext();
  const announcements: AnnouncementEntity[] = appContext.announcements;

  const announcementTitleMap = useMemo(() => {
    const map: Record<string, string> = {};
    announcements.forEach((a) => {
      if (a.id && a.title) map[a.id] = a.title;
    });
    return map;
  }, [announcements]);

  const fetchTestimonialsData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchGraphQLRequest<{ listTestimonials: TestimonialEntity[] }>({
        query: listTestimonials,
        variables: {},
        requestName: 'listTestimonials',
        requestType: 'query'
      });
      const items: TestimonialEntity[] = response?.listTestimonials || [];
      const activeItems = items.filter((t) => t.is_active !== false);
      const randomized = shuffleArray(activeItems).slice(0, maxItems);
      setTestimonials(randomized);
      setError(null);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      setError('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  }, [maxItems]);

  const goToNext = useCallback(() => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => {
        if (testimonials.length === 0) return 0;
        return (prev + 1) % testimonials.length;
      });
      setIsAnimating(false);
    }, 300);
  }, [testimonials.length]);

  const goToPrev = useCallback(() => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => {
        if (testimonials.length === 0) return 0;
        return (prev - 1 + testimonials.length) % testimonials.length;
      });
      setIsAnimating(false);
    }, 300);
  }, [testimonials.length]);

  const handleDotClick = useCallback((idx: number) => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex(idx);
      setIsAnimating(false);
    }, 300);
  }, []);

  useEffect(() => {
    fetchTestimonialsData();
  }, [fetchTestimonialsData]);

  // Auto-rotate
  useEffect(() => {
    if (testimonials.length <= 1) return;
    timerRef.current = setInterval(() => {
      goToNext();
    }, autoRotateInterval);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [testimonials.length, goToNext, autoRotateInterval]);

  if (loading) {
    return (
      <Container header={<Header variant="h2">Customer Testimonials</Header>}>
        <Box textAlign="center" padding={{ vertical: 'l' }}>
          <Spinner size="normal" />
        </Box>
      </Container>
    );
  }

  if (error || testimonials.length === 0) return null;

  const current = testimonials[currentIndex];

  return (
    <Container
      header={
        <Header variant="h2">
          <FontAwesomeIcon icon={faUserPen} />
          {` Customer Testimonials`}
        </Header>
      }
      fitHeight={true}
    >
      {/* Left arrow */}
      {testimonials.length > 1 && (
        <button className="testimonial-slider__arrow testimonial-slider__arrow--left" onClick={goToPrev} aria-label="Previous">
          ‹
        </button>
      )}
      <div className="testimonial-slider">
        {/* Content */}
        <div className={`testimonial-slider__content ${isAnimating ? 'testimonial-slider__content--fading' : ''}`}>
          {(() => {
            const parsed = parseTestimonialSummary(current.summary);
            if (parsed) {
              return (
                <>
                  <div className="testimonial-slider__title">{parsed.title}</div>
                  <div className="testimonial-slider__quote">&ldquo;{parsed.quote}&rdquo;</div>
                  {parsed.attribution && <div className="testimonial-slider__attribution">&mdash; {parsed.attribution}</div>}
                </>
              );
            }
            return <div className="testimonial-slider__text">{current.summary}</div>;
          })()}
        </div>

        {/* Dots indicator */}
        {testimonials.length > 1 && (
          <div className="testimonial-slider__dots">
            {testimonials.map((_, idx) => (
              <span
                key={idx}
                className={`testimonial-slider__dot ${idx === currentIndex ? 'testimonial-slider__dot--active' : ''}`}
                onClick={() => handleDotClick(idx)}
              />
            ))}
          </div>
        )}
      </div>
      {/* Right arrow */}
      {testimonials.length > 1 && (
        <button className="testimonial-slider__arrow testimonial-slider__arrow--right" onClick={goToNext} aria-label="Next">
          ›
        </button>
      )}
    </Container>
  );
};

export default TestimonialsCarousel;
